/* Round numbers to put a scale on a chart.
 *
 * A line with no scale beside it shows the shape of a move and nothing about
 * its size: the same wiggle is a dollar or a hundred, and there is no way to
 * tell which from the picture. What fixes that is two or three horizontal
 * gradations at values a person would have chosen — 25, 30, 35, never
 * 24.7311 — because the point of a gradation is to be read at a glance and an
 * arbitrary number has to be read digit by digit.
 *
 * So the step is picked from 1, 2, 2.5 and 5 times a power of ten, which is the
 * set that produces the numbers people actually write down, and the lines land
 * on multiples of it. How many come out is close to what was asked for rather
 * than exact — forcing an exact count is what produces 24.7311. */

const STEPS = [1, 2, 2.5, 5];

/** Every value of `step` inside the range, excluding both ends: a line printed
    along the very top or bottom of a chart reads as a border rather than as a
    reading. */
function walk(lo: number, hi: number, step: number): number[] {
  const out: number[] = [];
  // Rounded to what the step itself can carry, or a step of 2.5 leaves
  // 25.000000000000004 behind and that prints.
  const places = Math.max(0, -Math.floor(Math.log10(step)) + 1);
  for (let v = Math.ceil(lo / step) * step; v < hi; v += step) {
    if (v <= lo) continue;
    out.push(Number(v.toFixed(places)));
    if (out.length > 20) break;   // a guard, not a limit anybody should reach
  }
  return out;
}

/**
 * Values to draw a gradation at, inside `lo`..`hi` and never on the edges.
 *
 * Returns nothing where the span is zero or not finite: a flat line has no
 * range to divide, and one line through the middle of it would suggest a scale
 * that is not there.
 */
export function ticksFor(lo: number, hi: number, count = 3): number[] {
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || !(hi > lo)) return [];

  /* Every round step within two orders of magnitude of the obvious one, and
     then whichever of them lands nearest the number asked for.
     *
     * Taking the first step at least as large as span/count looked right and
     * was not: on 41,000 to 78,000 it jumps to 20,000 and draws a single line,
     * which is not a scale. The count is what matters, so the count is what is
     * searched on — and where two candidates are equally close, the coarser one
     * wins, because a chart this size would rather have one line too few than
     * one too many. */
  const rough = (hi - lo) / Math.max(1, count);
  const base = Math.floor(Math.log10(rough));

  let best: number[] | null = null;
  let bestScore = Infinity;
  for (const power of [base - 1, base, base + 1]) {
    for (const s of STEPS) {
      const ticks = walk(lo, hi, s * 10 ** power);
      if (ticks.length === 0) continue;
      const score = Math.abs(ticks.length - count);
      if (score < bestScore || (score === bestScore && best !== null && ticks.length < best.length)) {
        best = ticks;
        bestScore = score;
      }
    }
  }
  return best ?? [];
}
